import { ChangeDetectionStrategy, Component, ElementRef, NgZone, OnDestroy, inject } from '@angular/core';
import { ScrollDispatcher } from '@angular/cdk/scrolling';
import { Subscription } from 'rxjs';

/** Folga, em pixels, entre a borda do alvo e a borda da luz. */
const FOLGA = 6;

/** Raio usado quando o alvo não declara nenhum — o raio padrão do produto. */
const RAIO_PADRAO = 8;

/**
 * A "luz" sobre o alvo do passo.
 *
 * O truque é uma caixa do tamanho do alvo com uma sombra ENORME
 * (`0 0 0 100vmax`): a sombra escurece tudo o que está fora da caixa e nada do
 * que está dentro. Não há máscara, não há SVG, não há quatro retângulos
 * costurados em volta — e o alvo continua sendo o alvo de verdade, pintado
 * pelo próprio CSS dele, não uma cópia.
 *
 * **Geometria mora no TypeScript, não no template.** Posição, tamanho e raio
 * só existem em tempo de execução (dependem do `getBoundingClientRect()` do
 * alvo), e estilo inline em template reprova o portão
 * `mg-form-e-estilo-inline.pure.spec.ts` — que é a regra certa: o que está no
 * `.html` é aparência fixa, e aparência fixa é token.
 *
 * **É componente e não diretiva** porque não existe elemento hospedeiro para
 * decorar: a luz é um elemento NOVO, criado dentro do overlay do CDK, sem dono
 * no template de ninguém.
 */
@Component({
  selector: 'mg-tutorial-recorte',
  template: '',
  styleUrls: ['./recorte.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RecorteComponent implements OnDestroy {
  private readonly host = inject(ElementRef<HTMLElement>).nativeElement as HTMLElement;
  private readonly scrollDispatcher = inject(ScrollDispatcher);
  private readonly zone = inject(NgZone);

  private alvo: HTMLElement | null = null;
  private readonly inscricoes = new Subscription();
  private observador?: ResizeObserver;
  private readonly aoRedimensionarJanela = (): void => this.aplicar();

  constructor() {
    // Fora da zone: rolagem e resize disparam dezenas de vezes por segundo e
    // nada aqui muda ligação de template — só estilo do próprio elemento.
    // Dentro da zone, cada evento pediria uma detecção de mudanças da árvore
    // inteira, que é o custo clássico deste tipo de recurso.
    this.zone.runOutsideAngular(() => {
      this.inscricoes.add(this.scrollDispatcher.scrolled(0).subscribe(() => this.aplicar()));
      window.addEventListener('resize', this.aoRedimensionarJanela);
    });
  }

  ngOnDestroy(): void {
    this.inscricoes.unsubscribe();
    this.observador?.disconnect();
    window.removeEventListener('resize', this.aoRedimensionarJanela);
  }

  /** Passa a iluminar `alvo` e reposiciona sozinho enquanto ele estiver aceso. */
  focar(alvo: HTMLElement): void {
    this.alvo = alvo;

    this.observador?.disconnect();
    // O jsdom não tem `ResizeObserver` — sem a guarda, todo spec que monta este
    // componente estoura no construtor, e o que quebra não é o produto.
    if (typeof ResizeObserver !== 'undefined') {
      this.observador = new ResizeObserver(() => this.aplicar());
      this.observador.observe(alvo);
    }

    this.aplicar();
  }

  /** Mudanças no contêiner podem mover o alvo sem alterar seu tamanho. */
  atualizar(): void {
    this.aplicar();
  }

  /**
   * Escreve a geometria no elemento.
   *
   * O `rect` do alvo pode vir com altura zero quando o alvo é o HOST de um
   * componente (elemento inline com filho em bloco). Nesse caso a luz cai para
   * o primeiro filho elemento, que é quem de fato ocupa a caixa — sem isso o
   * recorte viraria um risco de 0px e a tela ficaria toda escura.
   */
  private aplicar(): void {
    if (!this.alvo) {
      return;
    }

    const medido = this.alvo.getBoundingClientRect();
    const primeiroFilho = this.alvo.firstElementChild;
    const caixa =
      (medido.width === 0 || medido.height === 0) && primeiroFilho
        ? primeiroFilho.getBoundingClientRect()
        : medido;

    const estilo = this.host.style;
    estilo.top = `${caixa.top - FOLGA}px`;
    estilo.left = `${caixa.left - FOLGA}px`;
    estilo.width = `${caixa.width + FOLGA * 2}px`;
    estilo.height = `${caixa.height + FOLGA * 2}px`;
    estilo.borderRadius = `${this.raioDoAlvo() + FOLGA}px`;
  }

  /** A luz acompanha o arredondamento do alvo — quadrado em botão, redondo em ícone. */
  private raioDoAlvo(): number {
    if (!this.alvo) {
      return RAIO_PADRAO;
    }

    const declarado = Number.parseFloat(getComputedStyle(this.alvo).borderTopLeftRadius);

    return Number.isFinite(declarado) && declarado > 0 ? declarado : RAIO_PADRAO;
  }
}
